import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { equals } from 'remeda';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { RestrictionsService } from 'src/app/restrictions/restrictions.service';
import { Restriction } from 'src/app/restrictions/types';
import { getFracName } from 'src/app/ui-components/pipes/frac-name.pipe';
import { AutopilotLevel, Frac } from '~lmo/models/frac.model';
import { FracMine } from '~lmo/models/mine.model';
import { BoxPickup, Order } from '~lmo/models/order.model';
import { Report } from '~lmo/models/report.model';
import { LmoOrdersService, LmoReportsService } from '~lmo/services';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { InfoBox } from '../../../map/google-maps-utility/infobox';
import { LightTheme } from '../../../map/mapStyles';

const fracAvailableColor = '#0000FF';
const fracStagingColor = '#0000FF';
const fracMineColor = '#00CC00';
const truckLoadedColor = '#448AFF';
const truckUnloadedColor = '#5F9A7E';
const driverHasntAcceptedColor = '#000000';
const onBoxMoveColor = '#f4c430';
const pendingBoxPickupColor = '#770000';
const boxPickupSiteColor = '#FFA500';

interface FracInfo {
  autopilotLevel: AutopilotLevel;
  available: boolean;
  id: number;
  lat: number;
  lng: number;
  name: string;
  outerLat?: number;
  outerLng?: number;
  outerRadius?: number;
  radius: number;
}

interface FracMineInfo {
  id: number;
  lat: number;
  lng: number;
  name: string;
  radius: number;
  isClosed: boolean;
  reportAvailable: boolean;
}

interface MapFracMineValue {
  fracMineInfo: FracMineInfo;
  circle: google.maps.Circle;
  nameBanner: InfoBox;
}

interface BoxPickupInfo {
  id: number;
  lat: number;
  lng: number;
  name: string;
  radius: number;
}

interface OrderInfo {
  id: number;
  lat: number;
  lng: number;
  loadNumber: number;
  loaded: boolean;
  meshType: string;
  status: 'dispatched' | 'driver_accepted';
  boxPickup: BoxPickup;
  pendingBoxPickup: boolean;
  onBoxMove: boolean;
}

interface MapOrderValue {
  orderInfo: OrderInfo;
  orderMarker: google.maps.Marker;
  boxPickupCircle: google.maps.Circle;
  insideActiveRestriction: boolean;
  boxPickupInfoBox: InfoBox;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-map',
  styleUrls: ['./lmo-map.component.scss'],
  templateUrl: './lmo-map.component.html',
})
export class LmoMapComponent implements OnInit {
  public changeBounds$$ = new BehaviorSubject<null>(null);
  private currentFrac: {
    fracInfo: FracInfo;
    dropOffCircle: google.maps.Circle;
    stagingCircle?: google.maps.Circle;
    nameBanner: InfoBox;
  } = null;
  private mapLocationBounds$$ = new BehaviorSubject<{ x1: number; x2: number; y1: number; y2: number }>(null);
  private mapFracMines: Record<number, MapFracMineValue> = {};
  private mapOrders: Record<number, MapOrderValue> = {};
  private subscriptions: Subscription[] = [];
  private map: google.maps.Map;
  private isMapInitialized$$ = new BehaviorSubject<boolean>(false);
  @ViewChild('map', { static: true }) private mapElement: ElementRef;
  private restrictionPolygons: Record<number, { restriction: Restriction; polygon: google.maps.Polygon }> = {};

  constructor(
    private fracService: LmoFracsService,
    private orderService: LmoOrdersService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private restrictionsService: RestrictionsService,
    private reports: LmoReportsService,
  ) {}

  public ngOnInit() {
    this.initMap();
    this.listenToFracChanges();
    this.listenToRestrictionChanges();
    this.changeBounds$$
      .asObservable()
      .pipe(debounceTime(200))
      .subscribe(() => {
        this.updateMapBounds();
      });
  }

  private initMap() {
    const mapConfig: google.maps.MapOptions = {
      center: { lat: 39.7392, lng: -104.9903 },
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: 3,
      },
      scaleControl: true,
      streetViewControl: false,
      styles: <any>LightTheme,
      zoom: 3,
      zoomControlOptions: {
        position: 3,
      },
    };

    this.map = new google.maps.Map(this.mapElement.nativeElement, mapConfig);
    google.maps.event.addListener(this.map, 'bounds_changed', () => {
      const bounds = this.map.getBounds();
      if (bounds) {
        const asJson = bounds.toJSON();
        this.mapLocationBounds$$.next({
          x1: asJson.west,
          x2: asJson.east,
          y1: asJson.south,
          y2: asJson.north,
        });
      }
    });
    this.isMapInitialized$$.next(true);
  }

  private listenToFracChanges() {
    const sub = this.isMapInitialized$$
      .asObservable()
      .pipe(
        filter((isMapInitialized) => isMapInitialized),
        switchMap(() =>
          combineLatest([
            this.fracService.currentFrac$,
            this.orderService.inProgress$,
            this.orderService.currentOrder$,
            this.reports.reports$,
          ]),
        ),
      )
      .subscribe(([frac, orders, order, reports]) => {
        const fracNeedsBoundUpdate = this.handleFracUpdates(frac);
        const minesNeedBoundUpdate = this.handleMineUpdates(frac, order, reports);
        const ordersNeedBoundUpdate = this.handleOrderUpdate(orders, order);

        if (fracNeedsBoundUpdate || minesNeedBoundUpdate || ordersNeedBoundUpdate) {
          this.changeBounds$$.next(null);
        }
      });

    this.subscriptions.push(sub);
  }

  private listenToRestrictionChanges() {
    const sub = this.mapLocationBounds$$
      .asObservable()
      .pipe(
        debounceTime(250),
        distinctUntilChanged(equals),
        filter((bounds) => !!bounds),
        switchMap((bounds) =>
          this.restrictionsService.getRestrictionsInBox(bounds.x1, bounds.x2, bounds.y1, bounds.y2),
        ),
      )
      .subscribe((restrictions) => {
        this.handleRestrictionUpdates(restrictions);
      });

    this.subscriptions.push(sub);
  }

  private handleFracUpdates(frac): boolean {
    if (!frac) {
      this.removeCurrentFrac();
      return true;
    }

    const newFracInfo = fracToFracInfo(frac);

    let needsBoundsUpdate = false;
    if (!this.currentFrac) {
      this.drawFrac(newFracInfo);
      needsBoundsUpdate = true;
    } else if (frac.id === this.currentFrac.fracInfo.id) {
      needsBoundsUpdate = this.updateFracIfNeeded(newFracInfo);
    } else {
      this.removeCurrentFrac();
      this.drawFrac(newFracInfo);
      needsBoundsUpdate = true;
    }
    return needsBoundsUpdate;
  }

  private handleRestrictionUpdates(restrictions: Restriction[]) {
    restrictions = restrictions.filter((r) => r.isActive === true);
    // remove the ones that are no longer there
    Object.values(this.restrictionPolygons).forEach((poly) => poly.polygon.setMap(null));
    this.restrictionPolygons = restrictions.reduce((acc, restriction) => {
      const polygon = new google.maps.Polygon({
        fillColor: restriction.applicableNow ? '#FF0000' : '#555555',
        fillOpacity: 0.25,
        map: this.map,
        paths: [restriction.polygon],
      });
      let infoWindowContent = `<div class="font-bold">${restriction.name}</div>`;
      (restriction.prettyPrintTimes || []).forEach((timeStr) => {
        infoWindowContent += `<div>${timeStr}</div>`;
      });
      if (restriction.applicableNow) {
        infoWindowContent += `<div class="text-lg font-bold flex justify-center">🚧 Active Now 🚧</div>`;
      }
      const infowindow = new google.maps.InfoWindow({
        content: infoWindowContent,
        pixelOffset: new google.maps.Size(0, -10),
      });
      polygon.addListener('mouseover', function(event: google.maps.MouseEvent) {
        infowindow.setPosition(event.latLng);
        infowindow.open((this as any).map);
      });

      polygon.addListener('mousemove', function(event: google.maps.MouseEvent) {
        infowindow.setPosition(event.latLng);
      });

      polygon.addListener('mouseout', function(event: google.maps.MouseEvent) {
        infowindow.close();
      });
      acc[restriction.id] = {
        polygon,
        restriction,
      };
      return acc;
    }, {} as Record<number, { restriction: Restriction; polygon: google.maps.Polygon }>);

    this.checkOrdersInsideRestrictions();
  }

  private drawFrac(newFracInfo: FracInfo) {
    const circlesOptions = fracInfoToFracMarker(newFracInfo);
    const fracNameBanner = fracInfoToFracName(newFracInfo);
    const newCurrentFrac = {
      dropOffCircle: new google.maps.Circle(circlesOptions.dropOffOptions),
      fracInfo: newFracInfo,
      nameBanner: fracNameBanner,
      stagingCircle: null,
    };
    if (circlesOptions.stagingOptions) {
      newCurrentFrac.stagingCircle = new google.maps.Circle(circlesOptions.stagingOptions);
    }
    newCurrentFrac.dropOffCircle.setMap(this.map);
    newCurrentFrac.nameBanner.setPosition(
      new google.maps.LatLng(
        newCurrentFrac.dropOffCircle
          .getBounds()
          .getNorthEast()
          .lat(),
        newCurrentFrac.dropOffCircle.getCenter().lng(),
      ),
    );
    newCurrentFrac.nameBanner.open(this.map);
    if (newCurrentFrac.stagingCircle) {
      newCurrentFrac.stagingCircle.setMap(this.map);
      if (wouldInnerCircleBeInsideOuterCircle(newCurrentFrac.dropOffCircle, newCurrentFrac.stagingCircle)) {
        newCurrentFrac.nameBanner.setPosition(
          new google.maps.LatLng(
            newCurrentFrac.stagingCircle
              .getBounds()
              .getNorthEast()
              .lat(),
            newCurrentFrac.stagingCircle.getCenter().lng(),
          ),
        );
      }
    }

    this.currentFrac = newCurrentFrac;
  }

  private updateFracIfNeeded(newFracInfo: FracInfo): boolean {
    if (!shallowEquals(newFracInfo, this.currentFrac.fracInfo)) {
      const circlesOptions = fracInfoToFracMarker(newFracInfo);
      this.currentFrac.dropOffCircle.setOptions(circlesOptions.dropOffOptions);

      if (circlesOptions.stagingOptions) {
        // Just need to update the staging circle
        if (this.currentFrac.stagingCircle) {
          this.currentFrac.stagingCircle.setOptions(circlesOptions.stagingOptions);
        } else {
          // The frac now has a staging circle and we need to draw it
          const stagingCircle = new google.maps.Circle(circlesOptions.stagingOptions);
          stagingCircle.setMap(this.map);
          this.currentFrac.stagingCircle = stagingCircle;
        }
      } else if (this.currentFrac.stagingCircle) {
        // The frac no longer has a staging circle
        this.currentFrac.stagingCircle.setMap(null);
        delete this.currentFrac.stagingCircle;
      }
      this.currentFrac.fracInfo = newFracInfo;
      return true;
    }
    return false;
  }

  private removeCurrentFrac() {
    if (this.currentFrac) {
      this.currentFrac.dropOffCircle.setMap(null);
      this.currentFrac.nameBanner.close();
      if (this.currentFrac.stagingCircle) {
        this.currentFrac.stagingCircle.setMap(null);
      }
      this.currentFrac = null;
    }
  }

  private handleMineUpdates(frac: Frac, currentOrder: Order, reports: Report[]): boolean {
    if (!frac || !frac.fracMines) {
      return false;
    }
    let fracMines: FracMine[];
    if (currentOrder) {
      fracMines = frac.fracMines.filter((fracMine) => fracMine.id === currentOrder.mine.id);
      const activeReport = reports.find(
        (r) =>
          r.available === false &&
          r.mesh &&
          r.mesh.id === currentOrder.mesh.id &&
          r.site.id === currentOrder.mine.site.id,
      );
      fracMines[0].reportAvailable = !activeReport;
    } else {
      fracMines = frac.fracMines;
    }
    const fracMineInfos = fracMines.map(fracMineToFracMineInfo);
    const fracMineRecord = convertArrayToIdRecord(fracMineInfos);
    const existingIds = Object.keys(this.mapFracMines);
    // remove all of the current draw fracMines that should no longer be there
    const fracMinesToBeRemoved: MapFracMineValue[] = existingIds
      .filter((key) => !fracMineRecord[key])
      .map((key) => this.mapFracMines[key]);
    fracMinesToBeRemoved.forEach((toBeRemoved) => this.removeFracMine(toBeRemoved));

    // Update the ones that need updating
    const fracMinesToBeUpdated = existingIds
      .filter(
        (key) =>
          fracMineRecord[key] &&
          !shallowEquals(
            fracMineRecord[key] as FracMineInfo,
            (this.mapFracMines[key] as MapFracMineValue).fracMineInfo,
          ),
      )
      .map((key) => ({ existing: this.mapFracMines[key], newFracMine: fracMineRecord[key] }));
    fracMinesToBeUpdated.forEach((toBeUpdated) => this.updateFracMine(toBeUpdated.newFracMine));

    // Draw the new ones
    const fracMinesToBeAdded = fracMineInfos.filter((fracMineInfo) => !this.mapFracMines[fracMineInfo.id]);
    fracMinesToBeAdded.forEach((fracMineInfo) => this.drawFracMine(fracMineInfo));

    return fracMinesToBeRemoved.length + fracMinesToBeUpdated.length + fracMinesToBeAdded.length > 0;
  }

  private drawFracMine(fracMineInfo: FracMineInfo) {
    const circle = new google.maps.Circle(fracMineInfoToCircleOptions(fracMineInfo));
    const nameBanner = fracMineToFracMineName(fracMineInfo);
    circle.setMap(this.map);
    nameBanner.setPosition(
      new google.maps.LatLng(
        circle
          .getBounds()
          .getNorthEast()
          .lat(),
        circle.getCenter().lng(),
      ),
    );
    nameBanner.open(this.map);
    this.mapFracMines[fracMineInfo.id] = { fracMineInfo, circle, nameBanner };
  }

  private updateFracMine(newFracMineInfo: FracMineInfo) {
    const existingFracMine = this.mapFracMines[newFracMineInfo.id];
    existingFracMine.circle.setOptions(fracMineInfoToCircleOptions(newFracMineInfo));
    existingFracMine.fracMineInfo = newFracMineInfo;
    this.drawFracMine(newFracMineInfo);
  }

  private removeFracMine(fracMine: { fracMineInfo: FracMineInfo; circle: google.maps.Circle; nameBanner: InfoBox }) {
    fracMine.circle.setMap(null);
    fracMine.nameBanner.close();
    delete this.mapFracMines[fracMine.fracMineInfo.id];
  }

  private handleOrderUpdate(orders: Order[], order: Order) {
    let ordersShouldBeOnMap: Order[];
    if (order) {
      ordersShouldBeOnMap = [order];
    } else {
      ordersShouldBeOnMap = orders;
    }

    const orderInfos = ordersShouldBeOnMap.map(orderToOrderInfo).filter((orderInfo) => !!orderInfo);
    const orderRecord = convertArrayToIdRecord(orderInfos);
    const existingIds = Object.keys(this.mapOrders);
    // remove all of the currently drawn orders that should no longer be there
    const ordersToBeRemoved: MapOrderValue[] = existingIds
      .filter((key) => !orderRecord[key])
      .map((key) => this.mapOrders[key]);
    ordersToBeRemoved.forEach((toBeRemoved) => this.removeOrder(toBeRemoved));

    // Update the ones that need updating
    const ordersToBeUpdated: OrderInfo[] = existingIds.filter((key) => orderRecord[key]).map((key) => orderRecord[key]);
    ordersToBeUpdated.forEach((toBeUpdated) => this.updateOrder(toBeUpdated));

    // Draw the new ones
    const ordersToBeAdded = orderInfos.filter((orderInfo) => !this.mapOrders[orderInfo.id]);
    ordersToBeAdded.forEach((orderInfo) => this.drawOrder(orderInfo));

    this.checkOrdersInsideRestrictions();

    return !!order && ordersToBeAdded.length > 0;
  }

  private drawOrder(orderInfo: OrderInfo) {
    const orderMarker = new google.maps.Marker(orderInfoToMarkerOptions(orderInfo));
    orderMarker.setMap(this.map);
    // Leaving this here in case we ever need to calibrate these markers
    // const calibrationMarker = new google.maps.Marker(orderInfoToMarkerOptionsForCalibration(orderInfo));
    // calibrationMarker.setMap(this.map);
    google.maps.event.addListener(orderMarker, 'click', () => {
      this.router.navigate([orderInfo.id], { relativeTo: this.activatedRoute });
    });

    let boxPickupInfoBox: InfoBox;
    let boxPickupCircle: google.maps.Circle;
    if (orderInfo.boxPickup) {
      const boxPickupInfo = boxPickupToBoxPickupInfo(orderInfo.boxPickup);
      boxPickupCircle = new google.maps.Circle(boxPickupInfoToCircleOptions(boxPickupInfo));
      boxPickupCircle?.setMap(this.map);

      boxPickupInfoBox = boxPickupInfoToInfoBox(boxPickupInfo);
      boxPickupInfoBox.setPosition(
        new google.maps.LatLng(
          boxPickupCircle
            ?.getBounds()
            .getNorthEast()
            .lat(),
          boxPickupCircle?.getCenter().lng(),
        ),
      );
      boxPickupInfoBox.open(this.map);
    }
    this.mapOrders[orderInfo.id] = {
      boxPickupCircle,
      boxPickupInfoBox,
      insideActiveRestriction: false,
      orderInfo,
      orderMarker,
    };
  }

  private updateOrder(newOrderInfo: OrderInfo) {
    const existingOrder = this.mapOrders[newOrderInfo.id];
    existingOrder.orderMarker.setOptions(orderInfoToMarkerOptions(newOrderInfo));
    if (existingOrder.boxPickupCircle && newOrderInfo.boxPickup) {
      existingOrder.boxPickupCircle?.setOptions(
        boxPickupInfoToCircleOptions(boxPickupToBoxPickupInfo(newOrderInfo.boxPickup)),
      );
    }
    existingOrder.orderInfo = newOrderInfo;
  }

  private removeOrder(order: MapOrderValue) {
    order.orderMarker.setMap(null);
    if (order.boxPickupInfoBox) {
      order.boxPickupCircle?.setMap(null);
      order.boxPickupInfoBox?.close();
    }
    delete this.mapOrders[order.orderInfo.id];
  }

  private updateMapBounds() {
    const bounds = new google.maps.LatLngBounds();
    if (this.currentFrac) {
      bounds.union(this.getFracBounds());
    }
    this.getFracMineBounds().forEach((bound) => {
      bounds.union(bound);
    });
    const orderKeys: MapOrderValue[] = Object.keys(this.mapOrders).map((orderId) => this.mapOrders[orderId]);
    if (orderKeys.length === 1) {
      orderKeys.forEach((order) => {
        bounds.extend(order.orderMarker.getPosition());
        if (order.boxPickupCircle) {
          bounds.union(this.getOrderBoxPickupBounds(order));
        }
      });
    }
    this.map.fitBounds(bounds);
  }

  private getFracBounds(): google.maps.LatLngBounds {
    if (this.currentFrac.stagingCircle) {
      return this.currentFrac.stagingCircle.getBounds();
    }
    return this.currentFrac.dropOffCircle.getBounds();
  }

  private getFracMineBounds(): google.maps.LatLngBounds[] {
    const fracMines = Object.keys(this.mapFracMines).map((key) => this.mapFracMines[key]);
    return fracMines.map((fracMine) => fracMine.circle.getBounds());
  }

  private getOrderBoxPickupBounds(order: MapOrderValue): google.maps.LatLngBounds {
    return order.boxPickupCircle?.getBounds();
  }

  private checkOrdersInsideRestrictions() {
    if (!this.mapOrders || !this.restrictionPolygons) {
      return;
    }
    Object.values(this.mapOrders).forEach((mapOrder) => {
      let insideActiveRestriction = false;
      const position = mapOrder.orderMarker.getPosition();
      for (const polyKey in this.restrictionPolygons) {
        if (!this.restrictionPolygons.hasOwnProperty(polyKey)) {
          continue;
        }
        const poly = this.restrictionPolygons[polyKey];
        if (!poly.restriction.applicableNow) {
          continue;
        }
        if (google.maps.geometry.poly.containsLocation(position, poly.polygon)) {
          insideActiveRestriction = true;
          break;
        }
      }

      if (insideActiveRestriction !== mapOrder.insideActiveRestriction) {
        const markerColor = getOrderMarkerColor(mapOrder.orderInfo);
        const stroke = insideActiveRestriction ? 'red' : 'white';
        const strokeWidth = insideActiveRestriction ? 4 : 2;
        const svgImage = getDataSvg(mapOrder.orderInfo.loadNumber, markerColor, stroke, strokeWidth);
        const icon = {
          ...(mapOrder.orderMarker.getIcon() as google.maps.Icon),
        };
        icon.url = svgImage;
        mapOrder.orderMarker.setIcon(icon);
        mapOrder.insideActiveRestriction = insideActiveRestriction;
      }
    });
  }
}

function fracToFracInfo(frac: Frac): FracInfo {
  return {
    autopilotLevel: frac.cluster.autopilotLevel,
    available: frac.available,
    id: frac.id,
    lat: frac.site.lngLat[1],
    lng: frac.site.lngLat[0],
    name: getFracName(frac),
    outerLat: frac.site.outerLngLat && frac.site.outerLngLat[1],
    outerLng: frac.site.outerLngLat && frac.site.outerLngLat[0],
    outerRadius: frac.site.outerRadius,
    radius: frac.site.radius,
  };
}

interface FracCircleOptions {
  dropOffOptions: google.maps.CircleOptions;
  stagingOptions?: google.maps.CircleOptions;
}

function fracInfoToFracMarker(fracInfo: FracInfo): FracCircleOptions {
  const color = fracAvailableColor;
  const dropOffOptions: google.maps.CircleOptions = {
    center: { lat: fracInfo.lat, lng: fracInfo.lng },
    fillColor: color,
    fillOpacity: 0.25,
    radius: fracInfo.radius,
    strokeColor: color,
    strokeOpacity: 0.5,
    strokeWeight: 2,
  };
  const fracCircles: FracCircleOptions = {
    dropOffOptions,
  };
  if (fracInfo.outerRadius && fracInfo.outerLat && fracInfo.outerLng) {
    fracCircles.stagingOptions = {
      center: { lat: fracInfo.outerLat, lng: fracInfo.outerLng },
      fillColor: fracStagingColor,
      fillOpacity: 0.35,
      radius: fracInfo.outerRadius,
      strokeColor: fracStagingColor,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      zIndex: 10,
    };
  }
  return fracCircles;
}

function fracInfoToFracName(fracInfo: FracInfo): InfoBox {
  return new InfoBox({
    alignBottom: true,
    boxStyle: {
      'background-color': fracInfo.available ? 'rgba(62, 62, 82, 0.9)' : 'rgba(119, 0, 0, 0.9)',
      'border-radius': '4px',
      color: 'white',
      'font-family': 'Averta-Regular, Roboto, serif',
      'font-size': '13px',
      width: '240px',
    },
    closeBoxURL: '',
    content: `
    <div style="display: flex; align-items: center; justify-items: center; padding: 4px">
      <div style="flex: 1; text-align: center;">${fracInfo.name}</div>
      <div style="flex: 0;"><img src="../../assets/icons/Rig.svg" alt="Icon"></div>
    </div>
    `,
    disableAutoPan: true,
    maxWidth: 240,
    pixelOffset: new google.maps.Size(-120, -5),
  });
}

function fracMineToFracMineInfo(mine: FracMine): FracMineInfo {
  return {
    id: mine.id,
    isClosed: mine.isClosed,
    lat: mine.site.lngLat[1],
    lng: mine.site.lngLat[0],
    name: mine.site.name,
    radius: mine.site.radius,
    reportAvailable: mine.reportAvailable,
  };
}

function fracMineInfoToCircleOptions(fracMineInfo: FracMineInfo): google.maps.CircleOptions {
  return {
    center: { lat: fracMineInfo.lat, lng: fracMineInfo.lng },
    fillColor: fracMineColor,
    fillOpacity: 0.25,
    radius: fracMineInfo.radius,
    strokeColor: fracMineColor,
    strokeOpacity: 0.5,
    strokeWeight: 2,
  };
}

function fracMineToFracMineName(fracMineInfo: FracMineInfo): InfoBox {
  return new InfoBox({
    alignBottom: true,
    boxStyle: {
      'background-color': fracMineInfo.reportAvailable === false ? 'rgba(119, 0, 0, 0.9)' : 'rgba(62, 62, 82, 0.9)',
      'border-radius': '4px',
      color: 'white',
      'font-family': 'Averta-Regular, Roboto, serif',
      'font-size': '13px',
      width: '240px',
    },
    closeBoxURL: '',
    content: `
    <div style="display: flex; align-items: center; justify-items: center; padding: 4px">
      <div style="flex: 1; text-align: center;">${fracMineInfo.name}</div>
    </div>
    `,
    disableAutoPan: true,
    maxWidth: 240,
    pixelOffset: new google.maps.Size(-120, -5),
  });
}

function boxPickupToBoxPickupInfo(boxPickup: BoxPickup): BoxPickupInfo {
  return {
    id: boxPickup.id,
    lat: boxPickup.lngLat[1],
    lng: boxPickup.lngLat[0],
    name: boxPickup.siteName,
    radius: boxPickup.siteRadius,
  };
}

function boxPickupInfoToCircleOptions(boxPickupInfo: BoxPickupInfo): google.maps.CircleOptions {
  return {
    center: { lat: boxPickupInfo.lat, lng: boxPickupInfo.lng },
    fillColor: boxPickupSiteColor,
    fillOpacity: 0.25,
    radius: boxPickupInfo.radius,
    strokeColor: boxPickupSiteColor,
    strokeOpacity: 0.5,
    strokeWeight: 2,
  };
}

function boxPickupInfoToInfoBox(boxPickupInfo: BoxPickupInfo): InfoBox {
  return new InfoBox({
    alignBottom: true,
    boxStyle: {
      'background-color': 'rgba(62, 62, 82, 0.9)',
      'border-radius': '4px',
      color: 'white',
      'font-family': 'Averta-Regular, Roboto, serif',
      'font-size': '13px',
      width: '240px',
    },
    closeBoxURL: '',
    content: `
      <div  style="display: flex; align-items: center; justify-items: center; padding: 4px">
        <div style="flex: 1; text-align: center;">(BOX PICKUP) ${boxPickupInfo.name}</div>
      </div>
    `,
    disableAutoPan: true,
    maxWidth: 140,
    pixelOffset: new google.maps.Size(-120, -5),
  });
}

function orderToOrderInfo(order: Order): OrderInfo {
  // uncomment this in case at some point they want to revert being able to see
  // completed order detail circle/marker on the map
  // if (!isInProgressGroup(order)) {
  //   return null;
  // }

  if (!order.user?.userStatus?.lastLngLat || order.user?.userStatus?.lastLngLat?.length < 2) {
    return null;
  }
  return {
    boxPickup: order.boxPickup,
    id: order.id,
    lat: order.user?.userStatus?.lastLngLat[1],
    lng: order.user?.userStatus?.lastLngLat[0],
    loadNumber: order.loadNumber,
    loaded: order.loaded,
    meshType: order.mesh.type,
    onBoxMove: order.onBoxMove,
    pendingBoxPickup: order.pendingBoxPickup,
    status: order.orderStatus as 'dispatched' | 'driver_accepted',
  };
}

function getOrderMarkerColor(orderInfo: OrderInfo): string {
  let color: string;
  if (orderInfo.status === 'driver_accepted') {
    color = orderInfo.onBoxMove
      ? onBoxMoveColor
      : orderInfo.loaded && !orderInfo.pendingBoxPickup
      ? truckLoadedColor
      : !orderInfo.loaded && !orderInfo.pendingBoxPickup
      ? truckUnloadedColor
      : orderInfo.pendingBoxPickup && !orderInfo.boxPickup?.completed
      ? pendingBoxPickupColor
      : truckUnloadedColor; // default color
  } else {
    color = driverHasntAcceptedColor;
  }
  return color;
}

function orderInfoToMarkerOptions(orderInfo: OrderInfo): google.maps.MarkerOptions {
  const color = getOrderMarkerColor(orderInfo);
  const options: google.maps.MarkerOptions = {
    icon: {
      anchor: new google.maps.Point(30, 30),
      labelOrigin: new google.maps.Point(30, 50),
      scaledSize: new google.maps.Size(60, 60),
      url: getDataSvg(`${orderInfo.loadNumber}`, color),
    },
    label: {
      fontSize: '12px;',
      text: orderInfo.meshType,
    },
    position: { lat: orderInfo.lat, lng: orderInfo.lng },
  };
  return options;
}

function convertArrayToIdRecord<T extends { id: number }>(array: T[]): Record<number, T> {
  return array.reduce((record, t) => {
    record[t.id] = t;
    return record;
  }, {});
}

function getDataSvg(innerText: string | number, color: string, stroke = 'white', strokeWidth = 2): string {
  const svg = `<svg width="82" height="82" viewBox="0 0 82 82" fill="none" xmlns="http://www.w3.org/2000/svg">\
  <g filter="url(#filter0_d)">\
  <path d="M41 59.8889C51.432 59.8889 59.8889 51.432 59.8889 41C59.8889 30.5679 51.432 22.1111 41 22.1111C30.5679 22.1111 22.1111 30.5679 22.1111 41C22.1111 51.432 30.5679 59.8889 41 59.8889Z" fill="${color}" stroke="${stroke}" stroke-width="${strokeWidth}"/>\
  <text font-family="sans-serif" font-size="14" text-anchor="middle" x="41" y="46" fill="white">${innerText}</text>
  </g>\
  <defs>\
  <filter id="filter0_d" x="0" y="0" width="82" height="82" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">\
  <feFlood flood-opacity="0" result="BackgroundImageFix"/>\
  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/>\
  <feOffset/>\
  <feGaussianBlur stdDeviation="10"/>\
  <feColorMatrix type="matrix" values="0 0 0 0 0.266667 0 0 0 0 0.541176 0 0 0 0 1 0 0 0 0.5 0"/>\
  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>\
  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>\
  </filter>\
  </defs>\
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8;base64,${btoa(svg)}`;
}

function shallowEquals(a: Record<string, any>, b: Record<string, any>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every((key) => a[key] === b[key]) && bKeys.every((key) => b[key] === a[key]);
}

function wouldInnerCircleBeInsideOuterCircle(inner: google.maps.Circle, outer: google.maps.Circle): boolean {
  const north = inner
    .getBounds()
    .getNorthEast()
    .lat();
  const south = inner
    .getBounds()
    .getSouthWest()
    .lat();
  const east = inner
    .getBounds()
    .getNorthEast()
    .lng();
  const west = inner
    .getBounds()
    .getSouthWest()
    .lng();
  const eastRadius = Math.abs(east - inner.getCenter().lng());
  const westRadius = Math.abs(east - inner.getCenter().lng());

  const points: google.maps.LatLngLiteral[] = [
    {
      // north
      lat: north,
      lng: inner.getCenter().lng(),
    },
    {
      // northwest
      lat: inner.getCenter().lat() + (westRadius * Math.SQRT2) / 2,
      lng: inner.getCenter().lng() - (westRadius * Math.SQRT2) / 2,
    },
    {
      // west
      lat: inner.getCenter().lat(),
      lng: west,
    },
    {
      // southwest
      lat: inner.getCenter().lat() - (westRadius * Math.SQRT2) / 2,
      lng: inner.getCenter().lng() - (westRadius * Math.SQRT2) / 2,
    },
    {
      // south
      lat: south,
      lng: inner.getCenter().lng(),
    },
    {
      // southeast
      lat: inner.getCenter().lat() - (eastRadius * Math.SQRT2) / 2,
      lng: inner.getCenter().lng() + (eastRadius * Math.SQRT2) / 2,
    },
    {
      // east
      lat: inner.getCenter().lat(),
      lng: east,
    },
    {
      // northeast
      lat: inner.getCenter().lat() - (eastRadius * Math.SQRT2) / 2,
      lng: inner.getCenter().lng() + (eastRadius * Math.SQRT2) / 2,
    },
  ];

  return points.every((point) => isInsideCircle(point, outer.getCenter().toJSON(), outer.getRadius() / 1000 /** km */));
}

function isInsideCircle(point: google.maps.LatLngLiteral, center: google.maps.LatLngLiteral, radius: number): boolean {
  const ky = 40000 / 360;
  const kx = Math.cos((Math.PI * center.lat) / 180.0) * ky;
  const dx = Math.abs(center.lng - point.lng) * kx;
  const dy = Math.abs(center.lat - point.lat) * ky;
  return Math.sqrt(dx * dx + dy * dy) <= radius;
}
